import { Button } from 'blaze-ui/Button'
import Styles from './StrategiesSelection.module.scss'
import { MultiStepStepLayout } from 'components/MultiStepFlow/MultiStepStepLayout/MultiStepStepLayout'
import { StickyFooterProvider } from 'providers/StickyFooterProvider/StickyFooterProvider'
import { FooterButtons, StickyFooter } from 'components/FirstTimeExperienceModal/components/Footer/Footer'
import { RadioGroup } from 'components/RadioGroup'
import { useEffect, useState } from 'react'
import {
  strategiesDefaultData,
  strategiesToContentStrategies,
  Strategy,
  buildContentStrategyTactics,
  numberWithCommas,
  mergeStrategyDefaults,
} from './utils'
import { Strategy as StrategyComponent } from './Strategy'
import { useUpdateBrandPlan } from 'hooks/queries/useUpdateBrandPlan'
import { BrandPlanTactic, BusinessType, Goal, SelectedBlogPlatform, SelectedEmailPlatform } from 'types'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { useBrandPlan } from 'hooks/queries/brandPlans/useBrandPlan'
import dayjs from 'dayjs'
import { useCreditsToGenerateContent } from 'hooks/useNeedsCreditsToGenerateContent'
import { CreditsPopover } from './CreditsPopover'
import { Text } from 'blaze-ui/Text/Text'
import { Heading } from 'blaze-ui/Heading/Heading'
import { ChevronDown } from 'blaze-ui/icons'
import { PaymentPlanKey } from 'api/clients/PaymentPlanClient'
import { observer } from 'mobx-react-lite'
import { TopBar } from '../TopBar/TopBar'
import { useContentDefaults } from 'hooks/queries/useContentDefaults'

export interface StrategiesSelectionProps {
  onNext: (brandPlanStrategies: Strategy[], planKey: PaymentPlanKey) => void
  onBack: () => void
  initialSelectedStrategies?: Strategy[]
  selectedBusinessType: BusinessType
  goal: Goal
  brandPlanId: number
}

export const StrategiesSelection = observer(
  ({
    onNext,
    onBack,
    initialSelectedStrategies,
    selectedBusinessType,
    goal,
    brandPlanId,
  }: StrategiesSelectionProps) => {
    const { data: brandPlan } = useBrandPlan(brandPlanId)
    const { data: contentDefaults } = useContentDefaults()
    const mergedDefaults = mergeStrategyDefaults(strategiesDefaultData, contentDefaults?.content_strategy_defaults)
    const [strategies, setStrategies] = useState<Strategy[]>(() => {
      if (initialSelectedStrategies) return initialSelectedStrategies
      return mergedDefaults[selectedBusinessType].strategies.map((strategy, index) => ({
        ...strategy,
        isSelected: index === 0,
      }))
    })

    const selectedStrategies = strategies.filter((strategy) => strategy.isSelected)

    const [selectedBlogPlatform, setSelectedBlogPlatform] = useState<SelectedBlogPlatform>(
      ExternalIntegrationEnum.WordPress,
    )
    const [selectedEmailPlatform, setSelectedEmailPlatform] = useState<SelectedEmailPlatform>(
      ExternalIntegrationEnum.Mailchimp,
    )

    useEffect(() => {
      if (brandPlan?.selected_blog_platform) {
        setSelectedBlogPlatform(brandPlan.selected_blog_platform)
      }
      if (brandPlan?.selected_email_platform) {
        setSelectedEmailPlatform(brandPlan.selected_email_platform)
      }
    }, [brandPlan?.selected_blog_platform, brandPlan?.selected_email_platform])

    useEffect(() => {
      if (initialSelectedStrategies?.some((strategy) => strategy.isSelected)) return

      const updatedStrategies = mergedDefaults[selectedBusinessType].strategies.map((strategy, index) => ({
        ...strategy,
        isSelected: index === 0,
      }))
      setStrategies(updatedStrategies)
    }, [initialSelectedStrategies, contentDefaults]) // eslint-disable-line react-hooks/exhaustive-deps

    const { mutate: updateBrandPlan, isLoading: isUpdatingBrandPlan } = useUpdateBrandPlan({ id: brandPlanId })

    const handleNext = () => {
      const selectedStrategiesValues = selectedStrategies.map((strategy) => ({
        ...strategy,
        tactics: buildContentStrategyTactics(strategy),
      }))

      updateBrandPlan(
        {
          content_strategies: strategiesToContentStrategies(selectedStrategiesValues),
          goal: goal,
          business_type: selectedBusinessType,
          start_date: dayjs().add(1, 'day').format('YYYY-MM-DD'),
          selected_blog_platform: selectedStrategiesValues.some((strategy) =>
            strategy.tactics.includes(BrandPlanTactic.SEO),
          )
            ? selectedBlogPlatform
            : null,
          selected_email_platform: selectedStrategiesValues.some((strategy) =>
            strategy.tactics.includes(BrandPlanTactic.Email),
          )
            ? selectedEmailPlatform
            : null,
        },
        {
          onSuccess: () => {
            onNext(
              strategies,
              isGrowthPlan ? PaymentPlanKey.BlazeAutopilotGrowth : PaymentPlanKey.BlazeAutopilotStarter,
            )
          },
        },
      )
    }

    const totalSocialCount = selectedStrategies.reduce((acc, strategy) => acc + strategy.crosspost_contents[0].count, 0)
    const totalBlogCount = selectedStrategies.reduce((acc, strategy) => acc + strategy.seo_contents[0].count, 0)
    const totalEmailCount = selectedStrategies.reduce((acc, strategy) => acc + strategy.email_contents[0].count, 0)
    const totalShortFormVideoCount = selectedStrategies.reduce(
      (acc, strategy) => acc + strategy.video_crosspost_contents[0].count,
      0,
    )

    const credits = useCreditsToGenerateContent(
      totalSocialCount,
      totalBlogCount,
      totalEmailCount,
      totalShortFormVideoCount,
      { applyVisualStyle: true, percentageOfVisualStylesGeneration: 1 },
    )
    const monthlyCredits = Math.floor(credits.creditsNeeded * 4.33)
    const isGrowthPlan = monthlyCredits >= 500

    return (
      <MultiStepStepLayout
        title="Here's your recommended content strategy"
        scrollContainerClassName={Styles.scrollContainer}
      >
        <StickyFooterProvider
          footer={
            <StickyFooter>
              <FooterButtons>
                <Button variant="tertiary" size="lg" onPress={onBack}>
                  Back
                </Button>
                <div className={Styles.footerRightContent}>
                  <CreditsPopover
                    blogCountCreditsNeeded={credits.creditsNeededPerContentType?.blog ?? 0}
                    emailCountCreditsNeeded={credits.creditsNeededPerContentType?.email ?? 0}
                    socialCountCreditsNeeded={credits.creditsNeededPerContentType?.social ?? 0}
                    shortFormVideoCountCreditsNeeded={credits.creditsNeededPerContentType?.shortFormVideo ?? 0}
                    totalSocialCount={totalSocialCount}
                    totalBlogCount={totalBlogCount}
                    totalEmailCount={totalEmailCount}
                    totalShortFormVideoCount={totalShortFormVideoCount}
                    monthlyCredits={monthlyCredits}
                    isGrowthPlan={isGrowthPlan}
                  >
                    <div className={Styles.footerNote}>
                      <Text variant="label" color="var(--dark-40)">
                        Estimated monthly credits
                      </Text>
                      <Heading level={5}>
                        {numberWithCommas(monthlyCredits)}
                        <ChevronDown size={12} />
                      </Heading>
                    </div>
                  </CreditsPopover>
                  <a href="https://www.blaze.ai/pricing" target="_blank" rel="noopener noreferrer">
                    <div className={Styles.footerNote}>
                      <Text variant="label" color="var(--dark-40)">
                        Recommended Plan
                      </Text>
                      <Heading level={5}>
                        {isGrowthPlan ? 'Growth' : 'Starter'} Plan
                        <ChevronDown size={12} />
                      </Heading>
                    </div>
                  </a>
                  <Button
                    size="lg"
                    onPress={handleNext}
                    isDisabled={isUpdatingBrandPlan || selectedStrategies.length === 0}
                  >
                    {isUpdatingBrandPlan ? 'Saving...' : `Continue with ${isGrowthPlan ? 'Growth' : 'Starter'} Plan`}
                  </Button>
                </div>
              </FooterButtons>
            </StickyFooter>
          }
        >
          <TopBar highlightStep="goalsAndPlan" />
          <RadioGroup
            value={selectedStrategies[0]?.id ?? ''}
            className={Styles.strategiesContainer}
            aria-label="Strategies"
            onChange={(value) => setStrategies(strategies.map((s) => ({ ...s, isSelected: s.id === value })))}
          >
            {strategies.map((strategy) => (
              <StrategyComponent
                key={strategy.id}
                strategy={strategy}
                onUpdate={(strategy) => {
                  setStrategies(strategies.map((s) => (s.id === strategy.id ? strategy : s)))
                }}
                onSelect={(strategyId) => {
                  const currentlySelected = strategies.find((s) => s.isSelected)?.id
                  if (currentlySelected === strategyId) {
                    setStrategies(strategies.map((s) => ({ ...s, isSelected: false })))
                  } else {
                    setStrategies(strategies.map((s) => ({ ...s, isSelected: s.id === strategyId })))
                  }
                }}
                selectedBlogPlatform={selectedBlogPlatform}
                setSelectedBlogPlatform={setSelectedBlogPlatform}
                selectedEmailPlatform={selectedEmailPlatform}
                setSelectedEmailPlatform={setSelectedEmailPlatform}
              />
            ))}
          </RadioGroup>
        </StickyFooterProvider>
      </MultiStepStepLayout>
    )
  },
)
